import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getBillingTypeFields, formatRackLocations, getRateUnit } from "./billingTypeConfig";
import { BY_THE_PALLET, BY_THE_PALLET_WAREHOUSE_BUILT } from "../../project/settings/utils/billingTypes";

export default function BillingInputsSection({ asset, storageCharges, isHistoricalData }) {
  const billingType = asset.billingType || storageCharges.billing_type;
  const relevantFields = getBillingTypeFields(billingType);
  
  // Get the charge details (e.g., normal_pallet, square_footage, etc.)
  const chargeKey = Object.keys(storageCharges).find(
    key => !['total', 'billing_type', 'calculated_at'].includes(key)
  );
  const chargeDetails = chargeKey ? storageCharges[chargeKey] : {};

  const renderField = (field) => {
    switch (field) {
      case 'data_source':
        return (
          <FieldDisplay 
            label="Data Source" 
            value={isHistoricalData ? 'Historical Data' : 'Current Data'} 
          />
        );
      
      case 'billing_type':
        return (
          <FieldDisplay 
            label="Billing Type" 
            value={billingType} 
          />
        );
      
      case 'rack_locations':
        const rackLocations = formatRackLocations(asset.rackLocations);
        return (
          <FieldDisplay
            label="Storage entries"
            value={rackLocations || 'No locations set'}
          />
        );
      
      case 'quantity':
        return (
          <FieldDisplay 
            label="Highmark Quantity" 
            value={asset.warehouseQuantity || 0} 
          />
        );
      
      case 'pallet_type':
        return (
          <FieldDisplay 
            label="Pallet Type" 
            value={asset.palletType || 'Not specified'} 
          />
        );

      case 'unit_type':
        return (
          <FieldDisplay
            label="Unit Type"
            value={asset.unitType || 'Not specified'}
          />
        );
      
      case 'square_footage':
        return (
          <FieldDisplay 
            label="Square Footage" 
            value={asset.squareFootage || '0'} 
          />
        );
      
      case 'cubic_footage':
        return (
          <FieldDisplay 
            label="Cubic Footage" 
            value={asset.cubicFootage || '0'} 
          />
        );
      
      case 'weight':
        return (
          <FieldDisplay 
            label="Weight" 
            value={asset.weight ? `${asset.weight} lbs` : '0 lbs'} 
          />
        );
      
      case 'dimensions':
        const dim = asset.dimensions?.[0] || {};
        return (
          <FieldDisplay 
            label="Dimensions" 
            value={`${dim.width || 0} × ${dim.depth || 0} × ${dim.height || 0}`} 
          />
        );
      
      case 'rate':
        const rateValue = (chargeDetails.rate_used || 0).toFixed(2);
        const rateUnit = getRateUnit(billingType);
        const isPalletBilling = billingType === BY_THE_PALLET || billingType === BY_THE_PALLET_WAREHOUSE_BUILT;
        const palletCount = chargeDetails.quantity;
        const palletText = palletCount > 1 ? 'pallets' : 'pallet';
        const formattedRate = isPalletBilling && palletCount != null
          ? `$${rateValue} per ${rateUnit}, ${palletCount} ${palletText}`
          : `$${rateValue} / ${rateUnit}`;
        return (
          <FieldDisplay
            label="Rate Applied"
            value={formattedRate}
          />
        );
      
      default:
        return null;
    }
  };

  // Check for co-located assets (exclude current asset from count)
  const allCoLocatedAssetIds = chargeDetails.co_located_asset_ids || [];
  const otherCoLocatedAssetIds = allCoLocatedAssetIds.filter(id => id !== asset.id);
  const hasCoLocatedAssets = otherCoLocatedAssetIds.length > 0;

  return (
    <Box display="flex" flexDirection="column" width="100%" maxWidth={600}>
      {relevantFields.map(field => (
        <React.Fragment key={field}>
          {renderField(field)}
        </React.Fragment>
      ))}

      {hasCoLocatedAssets && (
        <Box 
          sx={{ 
            mt: 2,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            <Box component="span" fontWeight={500}>Shared Storage Charge</Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This location is billed once and shared with {otherCoLocatedAssetIds.length} other {otherCoLocatedAssetIds.length === 1 ? 'item' : 'items'}. 
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function FieldDisplay({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" py={0.75} width="100%">
      <Typography variant="body1" fontSize="16px" color="text.primary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontSize="16px" fontWeight={400} color="text.primary" sx={{ textAlign: "right", ml: 2, whiteSpace: "pre-line" }}>
        {value}
      </Typography>
    </Box>
  );
}
